import {
  AdditiveBlending,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  NormalBlending,
  Object3D,
  RingGeometry,
  ShaderMaterial,
  TorusGeometry,
  Vector3,
} from 'three';
import { config } from '../config';

export interface SelectionZoneOptions {
  parent: Object3D;
  chamberCenter: Vector3;
  chamberRadius: number;
  chamberTopY: number;
}

export class SelectionZone {
  readonly group: Group;
  /** World Y position where the selected ball hovers (top of the beam, center of ring). */
  readonly hoverY: number;
  /** World position where the selected ball hovers. */
  readonly hoverPos: Vector3;

  private readonly ring: Mesh<TorusGeometry, MeshBasicMaterial>;
  private readonly ringGlow: Mesh<RingGeometry, MeshBasicMaterial>;
  private readonly beam: Mesh<CylinderGeometry, ShaderMaterial>;
  private intensity = 0.85;

  constructor(opts: SelectionZoneOptions) {
    this.group = new Group();
    this.group.name = 'selection-zone';
    opts.parent.add(this.group);

    const chamberR = opts.chamberRadius;
    const ringOuter = chamberR * config.selectionZone.ringOuterFactor;
    const tubeRadius = chamberR * config.selectionZone.ringThickness * 0.5;

    this.hoverY = opts.chamberCenter.y + chamberR * config.reveal.revealHoverFactor;
    this.hoverPos = new Vector3(opts.chamberCenter.x, this.hoverY, opts.chamberCenter.z);

    // Solid torus ring (always visible on any background).
    const torusGeom = new TorusGeometry(ringOuter, tubeRadius, 16, 64);
    const ringMat = new MeshBasicMaterial({
      color: config.selectionZone.color,
      transparent: true,
      opacity: this.intensity,
      blending: NormalBlending,
      side: DoubleSide,
    });
    this.ring = new Mesh(torusGeom, ringMat);
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.copy(this.hoverPos);
    this.group.add(this.ring);

    // Outer halo ring (additive — adds extra glow on darker materials behind it).
    const ringInner = chamberR * config.selectionZone.ringInnerFactor;
    const haloGeom = new RingGeometry(ringInner, ringOuter * 1.4, 64, 1);
    const haloMat = new MeshBasicMaterial({
      color: config.selectionZone.beamColor,
      transparent: true,
      opacity: 0.35,
      blending: AdditiveBlending,
      side: DoubleSide,
      depthWrite: false,
    });
    this.ringGlow = new Mesh(haloGeom, haloMat);
    this.ringGlow.rotation.x = -Math.PI / 2;
    this.ringGlow.position.copy(this.hoverPos);
    this.ringGlow.position.y -= tubeRadius * 0.5;
    this.group.add(this.ringGlow);

    // Volumetric beam (chamber-top → ring center).
    const beamHeight = Math.max(this.hoverY - opts.chamberTopY, 0.5);
    const beamRadius = chamberR * config.selectionZone.beamRadiusFactor;
    const beamGeom = new CylinderGeometry(beamRadius * 0.55, beamRadius, beamHeight, 32, 1, true);
    beamGeom.translate(0, beamHeight / 2, 0);

    const beamMat = new ShaderMaterial({
      uniforms: {
        uColor: { value: this.hexToVec3(config.selectionZone.beamColor) },
        uIntensity: { value: 0.55 },
      },
      vertexShader: /* glsl */ `
        varying float vY;
        varying vec3 vNormal;
        varying vec3 vViewDir;
        void main() {
          vY = uv.y;
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vNormal = normalize(normalMatrix * normal);
          vViewDir = normalize(cameraPosition - worldPos.xyz);
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        uniform float uIntensity;
        varying float vY;
        varying vec3 vNormal;
        varying vec3 vViewDir;
        void main() {
          // Vertical gradient: opaque at top (near ring), fades to zero at bottom (chamber).
          float vGrad = smoothstep(0.0, 1.0, vY);
          // Fresnel-like edge softening (cylinder edges fade out).
          float edge = 1.0 - abs(dot(normalize(vNormal), vViewDir));
          float alpha = vGrad * mix(0.35, 1.0, edge) * uIntensity;
          gl_FragColor = vec4(uColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      side: DoubleSide,
    });
    this.beam = new Mesh(beamGeom, beamMat);
    this.beam.position.set(opts.chamberCenter.x, opts.chamberTopY, opts.chamberCenter.z);
    this.group.add(this.beam);
  }

  /** Per-frame animation: slow ring spin + idle pulse. dt in seconds. */
  update(dt: number, time: number): void {
    this.ring.rotation.z += dt * config.selectionZone.ringSpinSpeedRad;
    this.ringGlow.rotation.z -= dt * config.selectionZone.ringSpinSpeedRad * 0.5;
    const pulse = 0.85 + 0.15 * Math.sin(time * 1.8);
    this.ring.material.opacity = Math.min(1, this.intensity * pulse);
    this.ringGlow.material.opacity = 0.35 * pulse * (this.intensity / 0.85);
  }

  /** Crank up the glow during a reveal. Call setIntensity(1) at reveal start, .6 at end. */
  setIntensity(level: number): void {
    this.intensity = level;
    (this.beam.material as ShaderMaterial).uniforms.uIntensity.value = level;
  }

  private hexToVec3(hex: number): { x: number; y: number; z: number } {
    return {
      x: ((hex >> 16) & 0xff) / 255,
      y: ((hex >> 8) & 0xff) / 255,
      z: (hex & 0xff) / 255,
    };
  }

  dispose(): void {
    this.ring.geometry.dispose();
    this.ring.material.dispose();
    this.ringGlow.geometry.dispose();
    this.ringGlow.material.dispose();
    this.beam.geometry.dispose();
    (this.beam.material as ShaderMaterial).dispose();
    this.group.parent?.remove(this.group);
  }
}
